import { MAP_TABS, type MapTab, type MapTabsSettings } from "@gettactic/api";

/** Product default: every tab visible, desks first. */
export const DEFAULT_MAP_TABS: MapTab[] = [...MAP_TABS];

const isMapTab = (value: string): value is MapTab =>
  (MAP_TABS as readonly string[]).includes(value);

/**
 * The map side-panel tabs to render, in order.
 *
 * Falls back to the product default whenever the organization has no usable
 * setting, so a missing, empty, or corrupt `map_tabs_settings` still yields a
 * working map rather than a panel with no tabs. Unknown and duplicate entries
 * are dropped, and a tab the setting never mentions is appended at the end —
 * a tab added to the product after an organization saved its order must not
 * silently disappear for them.
 */
export const getVisibleMapTabs = (
  settings: MapTabsSettings | undefined
): MapTab[] => {
  const configured = settings?.tabs;
  if (!configured?.length) {
    return DEFAULT_MAP_TABS;
  }

  const seen = new Set<MapTab>();
  const visible: MapTab[] = [];
  for (const entry of configured) {
    if (!entry?.tab || !isMapTab(entry.tab) || seen.has(entry.tab)) continue;
    seen.add(entry.tab);
    if (entry.enabled) {
      visible.push(entry.tab);
    }
  }

  for (const tab of DEFAULT_MAP_TABS) {
    if (!seen.has(tab)) {
      visible.push(tab);
    }
  }

  // Every tab hidden would leave the panel empty and the map unusable — treat
  // that as an unset setting rather than obeying it.
  return visible.length > 0 ? visible : DEFAULT_MAP_TABS;
};
